import React from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Alert, Box, Button, Collapse, Stack, Typography } from '@mui/material'
import { com } from '@eidu/web'
import FieldsStack from '../../components/data/FieldsStack'
import { requireNotUndefinedOrNull } from '../../util/require'
import useEntityType from '../../io/useEntityType'
import { useSortedEntityTypes } from '../../io/useEntityTypes'
import AuditLogDrawer from '../audit/AuditLogDrawer'
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import entityTypeIdFromString = com.eidu.sharedlib.entity.type.entityTypeIdFromString

type ViewEntityTypePageContentProps = {
  entityType: EntityType
  entityTypes: readonly EntityType[]
  onEdit: () => void
}

const ViewEntityTypePageContent = ({ entityType, entityTypes, onEdit }: ViewEntityTypePageContentProps) => (
  <Stack padding={3} spacing={3}>
    <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
      <Typography variant="h4">{entityType.name}</Typography>
      <Stack direction="row" spacing={1} alignItems="center">
        <AuditLogDrawer targetId={entityType.id.toString()} />
        <Button onClick={onEdit}>
          <Typography>Edit</Typography>
        </Button>
      </Stack>
    </Stack>
    <FieldsStack entityType={entityType} entityTypes={entityTypes} />
  </Stack>
)

type ViewEntityTypePageProps = {
  entityTypeId: EntityTypeId
}

const ViewEntityTypePage = ({ entityTypeId }: ViewEntityTypePageProps) => {
  const navigate = useNavigate()

  const { entityType: maybeEntityType, error: typeError } = useEntityType(entityTypeId)
  const entityType = maybeEntityType ?? undefined
  const entityTypes = useSortedEntityTypes() ?? []
  const error = typeError

  return (
    <Stack>
      <Collapse in={!!error}>
        <Alert
          variant="filled"
          severity="error"
          sx={{ margin: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => window.location.reload()}>
              Reload
            </Button>
          }
        >
          <Box>{String(error)}</Box>
          <Box>Please reload the page and try again.</Box>
        </Alert>
      </Collapse>
      {entityType && (
        <ViewEntityTypePageContent
          entityType={entityType}
          entityTypes={entityTypes}
          onEdit={() => navigate('edit', { relative: 'path' })}
        />
      )}
    </Stack>
  )
}

const ViewEntityTypePageRoute = () => {
  const params = useParams()
  const entityTypeId = entityTypeIdFromString(
    requireNotUndefinedOrNull(params.entityTypeId, 'Entity type ID is required')
  )

  return <ViewEntityTypePage entityTypeId={entityTypeId} />
}

export default ViewEntityTypePageRoute
